import { useState, useEffect } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { stripeApi } from '../../services/api';
import './Revenue.css';

const formatCurrency = (amount) => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${amount.toLocaleString()}`;
};

const Revenue = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [recentCharges, setRecentCharges] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);

  useEffect(() => {
    loadRevenueSummary();
  }, []);

  useEffect(() => {
    if (selectedCompany && selectedCompany !== 'all') {
      loadCompanyDetails(selectedCompany);
    }
  }, [selectedCompany]);

  const loadRevenueSummary = async () => {
    try {
      setLoading(true);
      const data = await stripeApi.getSummary();
      setSummary(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load revenue summary:', err);
      setError('Failed to load revenue data. Make sure Stripe API keys are configured.');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyDetails = async (company) => {
    try {
      const [charges, trend] = await Promise.all([
        stripeApi.getCharges(company, 20),
        stripeApi.getTrend(company, 12),
      ]);
      setRecentCharges(charges.charges || []);
      setRevenueTrend(trend.trend || []);
    } catch (err) {
      console.error('Failed to load company details:', err);
    }
  };

  // Calculate stats from real data
  const stats = summary?.totals ? [
    { label: 'Total MRR', value: formatCurrency(summary.totals.mrr), change: '', changeType: 'neutral' },
    { label: 'Total ARR', value: formatCurrency(summary.totals.arr), change: '', changeType: 'neutral' },
    { label: 'Active Subscriptions', value: summary.totals.activeSubscriptions.toString(), change: '', changeType: 'neutral' },
    { label: 'Total Customers', value: summary.totals.totalCustomers.toString(), change: '', changeType: 'neutral' },
  ] : [
    { label: 'Total MRR', value: '$0', change: '', changeType: 'neutral' },
    { label: 'Total ARR', value: '$0', change: '', changeType: 'neutral' },
    { label: 'Active Subscriptions', value: '0', change: '', changeType: 'neutral' },
    { label: 'Total Customers', value: '0', change: '', changeType: 'neutral' },
  ];

  // Build company breakdown from real data
  const companyBreakdown = summary?.companies?.map(company => ({
    name: company.company.charAt(0).toUpperCase() + company.company.slice(1),
    slug: company.company,
    mrr: company.mrr || 0,
    arr: company.arr || 0,
    subscriptions: company.activeSubscriptions || 0,
    customers: company.totalCustomers || 0,
    error: company.error,
    percentage: summary.totals.mrr > 0
      ? Math.round((company.mrr / summary.totals.mrr) * 100)
      : 0,
  })) || [];

  if (loading) {
    return (
      <div className="revenue-page">
        <div className="page-header">
          <h1 className="page-title">Revenue Analytics</h1>
        </div>
        <div className="loading-state">
          <Icon name="loader" size="xl" />
          <p>Loading revenue data from Stripe...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="revenue-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Revenue Analytics</h1>
          <p className="page-subtitle">Multi-company Stripe revenue reporting</p>
        </div>
        <div className="page-actions">
          <select
            className="company-selector"
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
          >
            <option value="all">All Companies</option>
            {companyBreakdown.map(company => (
              <option key={company.slug} value={company.slug}>{company.name}</option>
            ))}
          </select>
          <Button variant="secondary" icon="refresh-cw" onClick={loadRevenueSummary}>Refresh</Button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <Icon name="alert-triangle" />
          <span>{error}</span>
        </div>
      )}

      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      <div className="revenue-grid">
        {/* Revenue by Company */}
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">Revenue by Company</h3>
          </div>

          <div className="plan-breakdown">
            {companyBreakdown.length === 0 ? (
              <div className="empty-state">
                <p>No Stripe accounts connected. Add API keys to .env:</p>
                <code>STRIPE_KEY_CACHEE, STRIPE_KEY_REVMINE, STRIPE_KEY_Z101, STRIPE_KEY_BABYZILLA</code>
              </div>
            ) : (
              companyBreakdown.map((company, index) => (
                <div
                  key={index}
                  className={`plan-row ${company.error ? 'has-error' : ''} ${selectedCompany === company.slug ? 'selected' : ''}`}
                  onClick={() => setSelectedCompany(company.slug)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="plan-info">
                    <div className="plan-name">{company.name}</div>
                    <div className="plan-count">
                      {company.error ? (
                        <span className="error-text">{company.error}</span>
                      ) : (
                        <>{company.subscriptions} subs / {company.customers} customers</>
                      )}
                    </div>
                  </div>
                  <div className="plan-bar-container">
                    <div
                      className="plan-bar"
                      style={{ width: `${company.percentage}%` }}
                    />
                  </div>
                  <div className="plan-revenue">
                    {company.error ? '--' : formatCurrency(company.mrr)}
                    <span className="mrr-label">/mo</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Revenue Trend */}
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">
              {selectedCompany === 'all' ? 'Select a company to view trend' : `${selectedCompany.charAt(0).toUpperCase() + selectedCompany.slice(1)} Revenue Trend`}
            </h3>
          </div>

          {selectedCompany === 'all' ? (
            <div className="chart-placeholder">
              <Icon name="bar-chart-2" size="xl" />
              <p>Click a company above to view monthly trend</p>
            </div>
          ) : revenueTrend.length > 0 ? (
            <div className="trend-chart">
              {revenueTrend.map((month, index) => {
                const maxRevenue = Math.max(...revenueTrend.map(m => m.revenue));
                const barHeight = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
                return (
                  <div key={index} className="trend-bar-wrapper">
                    <div className="trend-bar" style={{ height: `${barHeight}%` }}>
                      <span className="trend-value">{formatCurrency(month.revenue)}</span>
                    </div>
                    <span className="trend-label">{month.month.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="chart-placeholder">
              <Icon name="loader" size="xl" />
              <p>Loading trend data...</p>
            </div>
          )}
        </section>
      </div>

      {/* Recent Charges */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">
            {selectedCompany === 'all' ? 'Recent Transactions' : `${selectedCompany.charAt(0).toUpperCase() + selectedCompany.slice(1)} Recent Charges`}
          </h3>
        </div>

        <div className="transactions-table-container">
          {selectedCompany === 'all' ? (
            <div className="empty-state">
              <p>Select a company to view recent charges</p>
            </div>
          ) : recentCharges.length === 0 ? (
            <div className="empty-state">
              <p>No recent charges found</p>
            </div>
          ) : (
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentCharges.map((charge) => (
                  <tr key={charge.id}>
                    <td className="tenant-cell">{charge.customer}</td>
                    <td>{charge.description || 'Charge'}</td>
                    <td className="amount-cell">${charge.amount.toLocaleString()}</td>
                    <td>
                      <span className={`status-badge status-${charge.status === 'succeeded' ? 'paid' : charge.status}`}>
                        {charge.status}
                      </span>
                    </td>
                    <td className="date-cell">{new Date(charge.created).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
};

export default Revenue;
